// HomeView-v2
import React, { useState, useMemo } from 'react';
import { topologyPath } from "insight/helpers/topologyPath";
import { viewHref } from "@shared/navigation/tabs/ViewsProvider";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts';
import {
  Shield,
  Cloud,
  Network,
  AlertTriangle,
  Building2,
  Search,
  X,
  FolderTree,
  Layers,
  Server,
  ChevronDown,
  ChevronRight,
  Globe,
  Lock,
  Filter,
  Cpu,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useInsightDataPath } from '../hooks/useInsightDataPath';
import { useAppSelector } from '@redux/hooks';
import { useCachedFetch } from '@hooks/useCachedFetch';
import { RefreshingIndicator } from '@shared/components/ListLoading';

// =============================================================================
// TYPES
// =============================================================================

interface FindingsSummary {
  critical: number;
  high: number;
  medium: number;
  low: number;
}

interface SubnetItem {
  id: string;
  name: string;
  cidr: string;
  availabilityZone: string | null;
  isPublic: boolean;
  availableIps?: number;
  totalIps?: number;
  state: string;
}

interface VpcSecurityItem {
  id: string;
  name: string;
  cloud: 'aws' | 'oci';
  region: string;
  accountId?: string;
  compartmentId?: string;
  accountName?: string;
  compartmentName?: string;
  cidr: string;
  securityScore: number;
  findingsSummary: FindingsSummary;
  subnetCount: number;
  instanceCount: number;
  subnets: SubnetItem[];
}

interface ComputeSummary {
  vcpusByCloud: { aws: number; oci: number };
  totalVcpus: number;
  awsVcpus: number;
  ociOcpus: number;
  ociVcpuEquivalent: number;
  topInstanceTypes?: {
    aws: Array<{ type: string; count: number; vcpus: number }>;
    oci: Array<{ shape: string; count: number; ocpus: number }>;
  };
}

interface InsightIndex {
  generatedAt: string;
  summary: {
    totalAccounts: number;
    totalVpcs: number;
    totalSubnets: number;
    totalInstances: number;
    totalFindings: number;
    globalSecurityScore: number;
  };
  accountsByCloud: { aws: number; oci: number };
  vpcsByCloud: { aws: number; oci: number };
  subnetsByCloud: { aws: number; oci: number };
  instancesByCloud: { aws: number; oci: number };
  securitySummary: {
    score: number;
    findings: FindingsSummary;
    totalFindings: number;
  };
  computeSummary?: ComputeSummary;
  vpcSecurityRanking: VpcSecurityItem[];
}

interface AccountInfo {
  id: string;
  name: string;
  cloud: 'aws' | 'oci';
  vpcCount: number;
  regions: string[];
}

// =============================================================================
// CONSTANTS
// =============================================================================

const CLOUD_COLORS = {
  aws: '#00676C',  // AWS dark teal
  oci: '#00CCCC',  // OCI bright cyan
};

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

const StatCard: React.FC<{
  title: string;
  value: number | string;
  icon: React.ReactNode;
  subtitle?: string;
}> = ({ title, value, icon, subtitle }) => (
  <div className="bg-semantic-bg-secondary rounded-2xl border border-semantic-border-primary p-6 shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-semantic-text-secondary">{title}</p>
        <p className="text-3xl font-bold text-semantic-text-primary mt-1">{value}</p>
        {subtitle && <p className="text-xs text-semantic-text-secondary mt-1">{subtitle}</p>}
      </div>
      <div className="w-12 h-12 bg-semantic-bg-tertiary rounded-xl flex items-center justify-center text-semantic-text-secondary">
        {icon}
      </div>
    </div>
  </div>
);

const CloudPieChart: React.FC<{
  title: string;
  data: { aws: number; oci: number };
  icon: React.ReactNode;
}> = ({ title, data, icon }) => {
  const chartData = [
    { name: 'AWS', value: data.aws, color: CLOUD_COLORS.aws },
    { name: 'OCI', value: data.oci, color: CLOUD_COLORS.oci },
  ].filter(d => d.value > 0);

  const total = data.aws + data.oci;

  if (total === 0) {
    return (
      <div className="bg-semantic-bg-secondary rounded-2xl border border-semantic-border-primary p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-semantic-text-primary">{title}</h3>
          <span className="text-semantic-text-secondary">{icon}</span>
        </div>
        <div className="h-48 flex items-center justify-center text-semantic-text-secondary">
          No data
        </div>
      </div>
    );
  }

  return (
    <div className="bg-semantic-bg-secondary rounded-2xl border border-semantic-border-primary p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-semantic-text-primary">{title}</h3>
        <span className="text-semantic-text-secondary">{icon}</span>
      </div>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={70}
              paddingAngle={2}
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: number) => [`${value} (${Math.round((value / total) * 100)}%)`, '']}
            />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="text-center mt-2">
        <span className="text-2xl font-bold text-semantic-text-primary">{total}</span>
        <span className="text-sm text-semantic-text-secondary ml-1">total</span>
      </div>
    </div>
  );
};

// Compute Comparison Card
const ComputeComparisonCard: React.FC<{
  computeSummary?: ComputeSummary;
  instancesByCloud: { aws: number; oci: number };
}> = ({ computeSummary, instancesByCloud }) => {
  if (!computeSummary) {
    return (
      <div className="bg-semantic-bg-secondary rounded-2xl border border-semantic-border-primary p-6 shadow-sm col-span-full">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-semantic-bg-brand-bold rounded-xl flex items-center justify-center">
            <Cpu className="w-5 h-5 text-semantic-fg-base" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-semantic-text-primary">Compute Capacity</h3>
            <p className="text-sm text-semantic-text-secondary">No compute data available</p>
          </div>
        </div>
      </div>
    );
  }

  const awsVcpus = computeSummary.awsVcpus;
  const ociOcpus = computeSummary.ociOcpus;
  const ociEquiv = computeSummary.ociVcpuEquivalent;
  const total = awsVcpus + ociEquiv;
  const awsPercent = total > 0 ? Math.round((awsVcpus / total) * 100) : 0;
  const ociPercent = total > 0 ? 100 - awsPercent : 0;

  return (
    <div className="bg-semantic-bg-secondary rounded-2xl border border-semantic-border-primary p-6 shadow-sm col-span-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-semantic-bg-brand-bold rounded-xl flex items-center justify-center">
            <Cpu className="w-5 h-5 text-semantic-fg-base" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-semantic-text-primary">Compute Capacity</h3>
            <p className="text-sm text-semantic-text-secondary">vCPUs across all clouds</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-semantic-text-primary">{total.toLocaleString()}</p>
          <p className="text-xs text-semantic-text-secondary">total vCPUs (normalized)</p>
        </div>
      </div>

      {/* Comparison Table */}
      <div className="overflow-hidden rounded-xl border border-semantic-border-primary">
        <table className="w-full">
          <thead>
            <tr className="bg-semantic-bg-tertiary">
              <th className="px-4 py-3 text-left text-xs font-semibold text-semantic-text-secondary uppercase">Cloud</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-semantic-text-secondary uppercase">Instances</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-semantic-text-secondary uppercase">vCPUs / OCPUs</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-semantic-text-secondary uppercase">Avg</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-semantic-text-secondary uppercase w-1/3">Distribution</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-semantic-border-secondary">
            <tr className="hover:bg-semantic-hover-primary">
              <td className="px-4 py-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#00676C]" />
                  <span className="font-medium text-semantic-text-primary">AWS</span>
                </div>
              </td>
              <td className="px-4 py-4 text-right font-mono text-semantic-text-secondary">
                {instancesByCloud.aws.toLocaleString()}
              </td>
              <td className="px-4 py-4 text-right">
                <span className="font-bold text-semantic-text-primary">{awsVcpus.toLocaleString()}</span>
                <span className="text-semantic-text-secondary text-sm ml-1">vCPUs</span>
              </td>
              <td className="px-4 py-4 text-right text-semantic-text-secondary">
                {instancesByCloud.aws > 0 ? (awsVcpus / instancesByCloud.aws).toFixed(1) : '0'} vCPU/inst
              </td>
              <td className="px-4 py-4">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-4 bg-semantic-bg-tertiary rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-[#00676C] transition-all duration-500"
                      style={{ width: `${awsPercent}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-semantic-text-secondary w-12 text-right">{awsPercent}%</span>
                </div>
              </td>
            </tr>
            <tr className="hover:bg-semantic-hover-danger">
              <td className="px-4 py-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#00CCCC]" />
                  <span className="font-medium text-semantic-text-primary">OCI</span>
                </div>
              </td>
              <td className="px-4 py-4 text-right font-mono text-semantic-text-secondary">
                {instancesByCloud.oci.toLocaleString()}
              </td>
              <td className="px-4 py-4 text-right">
                <span className="font-bold text-semantic-text-primary">{ociOcpus.toLocaleString()}</span>
                <span className="text-semantic-text-secondary text-sm ml-1">OCPUs</span>
                <span className="text-xs text-semantic-text-secondary ml-1">(~{ociEquiv.toLocaleString()} vCPU)</span>
              </td>
              <td className="px-4 py-4 text-right text-semantic-text-secondary">
                {instancesByCloud.oci > 0 ? (ociOcpus / instancesByCloud.oci).toFixed(1) : '0'} OCPU/inst
              </td>
              <td className="px-4 py-4">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-4 bg-semantic-bg-tertiary rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-[#00CCCC] transition-all duration-500"
                      style={{ width: `${ociPercent}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-semantic-text-secondary w-12 text-right">{ociPercent}%</span>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr className="bg-semantic-bg-tertiary">
              <td className="px-4 py-3 font-semibold text-semantic-text-secondary">Total</td>
              <td className="px-4 py-3 text-right font-mono font-semibold text-semantic-text-secondary">
                {(instancesByCloud.aws + instancesByCloud.oci).toLocaleString()}
              </td>
              <td className="px-4 py-3 text-right font-bold text-semantic-text-primary">
                {total.toLocaleString()} vCPUs
              </td>
              <td className="px-4 py-3"></td>
              <td className="px-4 py-3">
                <p className="text-xs text-semantic-text-secondary">* 1 OCPU = 2 vCPUs (normalized)</p>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

// Subnet Row
const SubnetRow: React.FC<{
  subnet: SubnetItem;
  onClick?: () => void;
  /** Where the row leads, for the "Add view" gestures (project 13D). */
  href?: string;
  isHighlighted?: boolean;
}> = ({ subnet, onClick, href, isHighlighted }) => (
  <tr
    {...viewHref(href)}
    className={`border-t border-semantic-border-secondary hover:bg-semantic-hover-brand cursor-pointer transition-colors ${
      isHighlighted ? 'bg-semantic-bg-warning-secondary' : 'bg-semantic-bg-tertiary/50'
    }`}
    onClick={onClick}
  >
    <td className="pl-12 pr-6 py-3">
      <div className="flex items-center gap-2">
        <Layers className="w-4 h-4 text-semantic-text-secondary" />
        <div>
          <p className="font-medium text-semantic-text-primary text-sm">{subnet.name}</p>
          <p className="text-xs text-semantic-text-secondary font-mono">
            {subnet.id.length > 40 ? subnet.id.slice(0, 40) + '...' : subnet.id}
          </p>
        </div>
      </div>
    </td>
    <td className="px-6 py-3">
      <code className="text-sm font-mono text-semantic-text-secondary bg-semantic-bg-tertiary px-2 py-0.5 rounded">
        {subnet.cidr}
      </code>
    </td>
    <td className="px-6 py-3 text-sm text-semantic-text-secondary">
      {subnet.availabilityZone || '-'}
    </td>
    <td className="px-6 py-3">
      {subnet.isPublic ? (
        <span className="flex items-center gap-1 text-semantic-text-warning text-sm">
          <Globe className="w-4 h-4" /> Public
        </span>
      ) : (
        <span className="flex items-center gap-1 text-semantic-text-secondary text-sm">
          <Lock className="w-4 h-4" /> Private
        </span>
      )}
    </td>
    <td className="px-6 py-3 text-sm text-semantic-text-secondary">
      {subnet.availableIps !== undefined && subnet.totalIps !== undefined ? (
        <span>
          {subnet.availableIps} / {subnet.totalIps} IPs
        </span>
      ) : (
        '-'
      )}
    </td>
    <td className="px-6 py-3">
      <span className={`text-xs px-2 py-0.5 rounded-full ${
        subnet.state === 'available' || subnet.state === 'AVAILABLE'
          ? 'bg-semantic-bg-success-secondary text-semantic-text-success'
          : 'bg-semantic-bg-tertiary text-semantic-text-secondary'
      }`}>
        {subnet.state}
      </span>
    </td>
    <td className="px-6 py-3"></td>
  </tr>
);

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const HomeViewV2: React.FC = () => {
  const navigate = useNavigate();
  const dataPath = useInsightDataPath();
  
  const { tenantId } = useAppSelector((state) => state.selection);

  // Cached per tenant and data path: coming back to the dashboard (a view switch, the
  // menu, Back) paints the last index at once and refreshes it underneath. The tenant is
  // in the key even though today's path is fixed, so the index never outlives the lens it
  // was loaded under.
  const {
    data,
    error,
    isLoading: loading,
    isRefreshing,
  } = useCachedFetch<InsightIndex>(
    `insight/insight-index?tenantId=${tenantId}&dataPath=${encodeURIComponent(dataPath)}`,
    async () => {
      const res = await fetch(`${dataPath}/insight-index.json`);
      if (!res.ok) throw new Error('Failed to load insight index');
      return res.json();
    }
  );

  // Filter by selected account/compartment
  const [selectedAccount, setSelectedAccount] = useState<string | null>(null);

  // VPC Table state
  const [expandedVpcs, setExpandedVpcs] = useState<Set<string>>(new Set());
  const [tableSearchQuery, setTableSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    cloud: '' as '' | 'aws' | 'oci',
    severity: '' as '' | 'critical' | 'high' | 'medium' | 'low' | 'any',
  });
  const [showFilters, setShowFilters] = useState(false);

  // ==========================================================================
  // EXTRACT ACCOUNTS/COMPARTMENTS FROM VPCs
  // ==========================================================================
  const awsAccounts = useMemo<AccountInfo[]>(() => {
    if (!data?.vpcSecurityRanking) return [];
    
    const accountMap = new Map<string, AccountInfo>();
    
    data.vpcSecurityRanking
      .filter(vpc => vpc.cloud === 'aws' && vpc.accountId)
      .forEach(vpc => {
        const id = vpc.accountId;
        if (!id) return;
        if (accountMap.has(id)) {
          const existing = accountMap.get(id);
          if (existing) {
            existing.vpcCount++;
            if (!existing.regions.includes(vpc.region)) {
              existing.regions.push(vpc.region);
            }
          }
        } else {
          accountMap.set(id, {
            id,
            name: vpc.accountName || id,
            cloud: 'aws',
            vpcCount: 1,
            regions: [vpc.region],
          });
        }
      });
    
    return Array.from(accountMap.values());
  }, [data?.vpcSecurityRanking]);

  const ociCompartments = useMemo<AccountInfo[]>(() => {
    if (!data?.vpcSecurityRanking) return [];
    
    const compartmentMap = new Map<string, AccountInfo>();
    
    data.vpcSecurityRanking
      .filter(vpc => vpc.cloud === 'oci' && vpc.compartmentId)
      .forEach(vpc => {
        const id = vpc.compartmentId;
        if (!id) return;
        if (compartmentMap.has(id)) {
          const existing = compartmentMap.get(id);
          if (existing) {
            existing.vpcCount++;
            if (!existing.regions.includes(vpc.region)) {
              existing.regions.push(vpc.region);
            }
          }
        } else {
          compartmentMap.set(id, {
            id,
            name: vpc.compartmentName || id,
            cloud: 'oci',
            vpcCount: 1,
            regions: [vpc.region],
          });
        }
      });
    
    return Array.from(compartmentMap.values());
  }, [data?.vpcSecurityRanking]);

  // All VPCs
  const vpcs = useMemo(() => data?.vpcSecurityRanking || [], [data?.vpcSecurityRanking]);

  // ==========================================================================
  // FILTERED VPCs FOR TABLE
  // ==========================================================================
  const filteredVpcs = useMemo(() => {
    let result = [...vpcs];
    
    // Filter by selected account
    if (selectedAccount) {
      result = result.filter(vpc => 
        vpc.accountId === selectedAccount || 
        vpc.compartmentId === selectedAccount
      );
    }

    // Cloud filter
    if (filters.cloud) {
      result = result.filter(vpc => vpc.cloud === filters.cloud);
    }

    // Severity filter
    if (filters.severity) {
      if (filters.severity === 'any') {
        // Any findings (at least one finding of any severity)
        result = result.filter(vpc => {
          const f = vpc.findingsSummary;
          return f && (f.critical > 0 || f.high > 0 || f.medium > 0 || f.low > 0);
        });
      } else {
        // Filter by specific severity (has at least one finding of that level)
        result = result.filter(vpc => {
          const f = vpc.findingsSummary;
          return f && f[filters.severity as keyof FindingsSummary] > 0;
        });
      }
    }

    // Table search query (also searches by account/compartment name)
    if (tableSearchQuery.trim()) {
      const query = tableSearchQuery.toLowerCase().trim();
      result = result.filter(vpc =>
        vpc.name.toLowerCase().includes(query) ||
        vpc.id.toLowerCase().includes(query) ||
        vpc.region.toLowerCase().includes(query) ||
        vpc.cidr.includes(query) ||
        vpc.accountName?.toLowerCase().includes(query) ||
        vpc.compartmentName?.toLowerCase().includes(query)
      );
    }

    return result;
  }, [vpcs, selectedAccount, filters, tableSearchQuery]);

  // Toggle VPC expansion
  const toggleExpand = (vpcId: string) => {
    setExpandedVpcs(prev => {
      const next = new Set(prev);
      if (next.has(vpcId)) {
        next.delete(vpcId);
      } else {
        next.add(vpcId);
      }
      return next;
    });
  };

  // Handle VPC click (navigate to topology)
  const handleVpcClick = (vpc: VpcSecurityItem) => {
    navigate(topologyPath(vpc));
  };

  // Handle subnet click
  const handleSubnetClick = (subnet: SubnetItem, vpc: VpcSecurityItem) => {
    navigate(topologyPath(vpc, subnet.id));
  };

  const clearFilters = () => {
    setTableSearchQuery('');
    setFilters({ cloud: '', severity: '' });
    setSelectedAccount(null);
  };

  const hasActiveFilters = tableSearchQuery || filters.cloud || filters.severity || selectedAccount;

  // ==========================================================================
  // LOADING / ERROR STATES
  // ==========================================================================
  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-semantic-bg-brand-bold"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-semantic-text-danger mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-semantic-text-primary">Failed to load dashboard</h2>
          <p className="text-semantic-text-secondary mt-2">{error || 'No data available'}</p>
          <p className="text-sm text-semantic-text-secondary mt-4">
            Run: <code className="bg-semantic-bg-tertiary px-2 py-1 rounded text-semantic-text-primary">node scarif/generateInsightIndex.js</code>
          </p>
        </div>
      </div>
    );
  }

  // ==========================================================================
  // RENDER
  // ==========================================================================
  return (
    <div className="relative bg-semantic-bg-primary min-h-screen">
      <RefreshingIndicator active={isRefreshing} />
      <div className="p-6">
        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Accounts / Compartments"
            value={data.summary.totalAccounts}
            icon={<Building2 className="w-6 h-6 text-semantic-text-secondary" />}
            subtitle={`${data.accountsByCloud.aws} AWS, ${data.accountsByCloud.oci} OCI`}
          />
          <StatCard
            title="VPCs / VCNs"
            value={data.summary.totalVpcs}
            icon={<Cloud className="w-6 h-6 text-semantic-text-secondary" />}
            subtitle={`${data.vpcsByCloud.aws} AWS, ${data.vpcsByCloud.oci} OCI`}
          />
          <StatCard
            title="Subnets"
            value={data.summary.totalSubnets}
            icon={<Network className="w-6 h-6 text-semantic-text-secondary" />}
            subtitle={`${data.subnetsByCloud.aws} AWS, ${data.subnetsByCloud.oci} OCI`}
          />
          <StatCard
            title="Security Findings"
            value={data.summary.totalFindings}
            icon={<Shield className="w-6 h-6 text-semantic-text-secondary" />}
            subtitle={`${data.securitySummary.findings.critical} critical`}
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <CloudPieChart
            title="Accounts by Cloud"
            data={data.accountsByCloud}
            icon={<Building2 className="w-5 h-5 text-semantic-text-secondary" />}
          />
          <CloudPieChart
            title="VPCs by Cloud"
            data={data.vpcsByCloud}
            icon={<Cloud className="w-5 h-5 text-semantic-text-secondary" />}
          />
          <CloudPieChart
            title="Subnets by Cloud"
            data={data.subnetsByCloud}
            icon={<Network className="w-5 h-5 text-semantic-text-secondary" />}
          />
        </div>

        {/* Compute Comparison Card */}
        <div className="mb-8">
          <ComputeComparisonCard 
            computeSummary={data.computeSummary}
            instancesByCloud={data.instancesByCloud}
          />
        </div>

        {/* Filter Badge (when account/compartment is selected) */}
        {selectedAccount && (
          <div className="flex items-center gap-2 mb-4">
            <span className="text-sm text-semantic-text-secondary">Filtered by:</span>
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-semantic-bg-brand-secondary text-semantic-text-brand rounded-lg text-sm">
              {[...awsAccounts, ...ociCompartments].find(a => a.id === selectedAccount)?.name || selectedAccount.slice(-12)}
              <button 
                onClick={() => setSelectedAccount(null)}
                className="hover:bg-semantic-hover-brand rounded p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          </div>
        )}

        {/* ================================================================= */}
        {/* VPC TABLE */}
        {/* ================================================================= */}
        <div className="bg-semantic-bg-secondary rounded-2xl border border-semantic-border-primary shadow-sm overflow-hidden">
          {/* Header with Search and Filters */}
          <div className="px-6 py-4 border-b border-semantic-border-secondary">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-semantic-text-primary">VPCs / VCNs</h3>
                <p className="text-sm text-semantic-text-secondary">
                  {filteredVpcs.length} of {vpcs.length} VPCs/VCNs
                </p>
              </div>
            </div>

            {/* Table Search Bar */}
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-semantic-text-secondary" />
                <input
                  type="text"
                  placeholder="Search by name, CIDR, region, account..."
                  value={tableSearchQuery}
                  onChange={(e) => setTableSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-semantic-border-primary rounded-xl focus:outline-none focus:ring-2 focus:ring-semantic-outline-primary focus:border-transparent text-sm bg-semantic-bg-secondary text-semantic-text-primary placeholder:text-semantic-text-secondary"
                />
                {tableSearchQuery && (
                  <button
                    onClick={() => setTableSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-semantic-text-secondary hover:text-semantic-text-primary"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2.5 border rounded-xl text-sm font-medium transition-colors ${
                  showFilters || hasActiveFilters
                    ? 'border-semantic-border-brand-secondary bg-semantic-bg-brand-secondary text-semantic-text-brand'
                    : 'border-semantic-border-primary text-semantic-text-secondary hover:bg-semantic-bg-tertiary'
                }`}
              >
                <Filter className="w-4 h-4" />
                Filters
                {hasActiveFilters && (
                  <span className="w-2 h-2 bg-semantic-bg-brand-bold rounded-full" />
                )}
              </button>

              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="px-3 py-2.5 text-sm text-semantic-text-secondary hover:text-semantic-text-primary"
                >
                  Clear all
                </button>
              )}
            </div>

            {/* Filter Options */}
            {showFilters && (
              <div className="flex items-center gap-4 mt-4 pt-4 border-t border-semantic-border-secondary">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-semantic-text-secondary">Cloud:</span>
                  <select
                    value={filters.cloud}
                    onChange={(e) => setFilters(f => ({ ...f, cloud: e.target.value as '' | 'aws' | 'oci' }))}
                    className="border border-semantic-border-primary rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-semantic-outline-primary bg-semantic-bg-secondary text-semantic-text-primary"
                  >
                    <option value="">All</option>
                    <option value="aws">AWS</option>
                    <option value="oci">OCI</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-semantic-text-secondary">Findings:</span>
                  <select
                    value={filters.severity}
                    onChange={(e) => setFilters(f => ({ ...f, severity: e.target.value as '' | 'critical' | 'high' | 'medium' | 'low' | 'any' }))}
                    className="border border-semantic-border-primary rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-semantic-outline-primary bg-semantic-bg-secondary text-semantic-text-primary"
                  >
                    <option value="">All</option>
                    <option value="any">Has any findings</option>
                    <option value="critical">Has Critical</option>
                    <option value="high">Has High</option>
                    <option value="medium">Has Medium</option>
                    <option value="low">Has Low</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Table */}
          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full">
              <thead className="sticky top-0 z-10 bg-semantic-bg-tertiary">
                <tr className="text-left">
                  <th className="px-6 py-3 text-xs font-semibold text-semantic-text-secondary uppercase tracking-wider">
                    VPC / VCN
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-semantic-text-secondary uppercase tracking-wider">
                    Account / Compartment
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-semantic-text-secondary uppercase tracking-wider">
                    Cloud
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-semantic-text-secondary uppercase tracking-wider">
                    Region
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-semantic-text-secondary uppercase tracking-wider">
                    Resources
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-semantic-text-secondary uppercase tracking-wider">
                    Findings
                  </th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-semantic-border-secondary">
                {filteredVpcs.map((vpc) => {
                  const isExpanded = expandedVpcs.has(vpc.id);

                  return (
                    <React.Fragment key={vpc.id}>
                      {/* VPC Row */}
                      <tr
                        className="hover:bg-semantic-bg-tertiary cursor-pointer transition-colors"
                        onClick={() => toggleExpand(vpc.id)}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <button className="text-semantic-text-secondary hover:text-semantic-text-primary">
                              {isExpanded ? (
                                <ChevronDown className="w-5 h-5" />
                              ) : (
                                <ChevronRight className="w-5 h-5" />
                              )}
                            </button>
                            <div>
                              <p className="font-medium text-semantic-text-primary">{vpc.name}</p>
                              <p className="text-xs text-semantic-text-secondary">
                                <code className="font-mono">{vpc.cidr}</code>
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const accountId = vpc.cloud === 'aws' ? vpc.accountId : vpc.compartmentId;
                              if (accountId) {
                                setSelectedAccount(accountId);
                              }
                            }}
                            className="group flex items-center gap-2 text-left hover:bg-semantic-bg-tertiary px-2 py-1 -mx-2 -my-1 rounded-lg transition-colors"
                          >
                            {vpc.cloud === 'aws' ? (
                              <Building2 className="w-4 h-4 text-semantic-text-warning" />
                            ) : (
                              <FolderTree className="w-4 h-4 text-semantic-text-danger" />
                            )}
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-semantic-text-primary truncate max-w-[180px] group-hover:text-semantic-text-brand">
                                {vpc.cloud === 'aws' ? (vpc.accountName || vpc.accountId || '-') : (vpc.compartmentName || (vpc.compartmentId ? vpc.compartmentId.slice(-12) + '...' : '-'))}
                              </p>
                              <p className="text-xs text-semantic-text-secondary truncate max-w-[180px]">
                                {vpc.cloud === 'aws' ? 'AWS Account' : 'OCI Compartment'}
                              </p>
                            </div>
                          </button>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                              vpc.cloud === 'aws'
                                ? 'bg-semantic-bg-warning-secondary text-semantic-text-warning'
                                : 'bg-semantic-bg-danger-secondary text-semantic-text-danger'
                            }`}
                          >
                            <Cloud className="w-3 h-3" />
                            {vpc.cloud.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-semantic-text-secondary">{vpc.region}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3 text-xs text-semantic-text-secondary">
                            <span className="flex items-center gap-1">
                              <Layers className="w-3 h-3" /> {vpc.subnetCount} subnets
                            </span>
                            <span className="flex items-center gap-1">
                              <Server className="w-3 h-3" /> {vpc.instanceCount}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {vpc.findingsSummary && (vpc.findingsSummary.critical > 0 || vpc.findingsSummary.high > 0 || vpc.findingsSummary.medium > 0 || vpc.findingsSummary.low > 0) ? (
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {vpc.findingsSummary.critical > 0 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-semantic-bg-danger-secondary text-semantic-text-danger">
                                  C: {vpc.findingsSummary.critical}
                                </span>
                              )}
                              {vpc.findingsSummary.high > 0 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-semantic-bg-warning-secondary text-semantic-text-warning">
                                  H: {vpc.findingsSummary.high}
                                </span>
                              )}
                              {vpc.findingsSummary.medium > 0 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-semantic-bg-warning-secondary text-semantic-text-warning">
                                  M: {vpc.findingsSummary.medium}
                                </span>
                              )}
                              {vpc.findingsSummary.low > 0 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-semantic-bg-success-secondary text-semantic-text-success">
                                  L: {vpc.findingsSummary.low}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-semantic-text-secondary">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <button
                            {...viewHref(topologyPath(vpc))}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleVpcClick(vpc);
                            }}
                            className="text-semantic-text-brand hover:opacity-80 text-sm font-medium"
                          >
                            View
                          </button>
                        </td>
                      </tr>

                      {/* Expanded Subnets */}
                      {isExpanded && vpc.subnets && vpc.subnets.length > 0 && (
                        <>
                          {/* Subnet Header */}
                          <tr className="bg-semantic-bg-tertiary/50">
                            <td colSpan={7} className="px-6 py-2">
                              <div className="grid grid-cols-7 text-xs text-semantic-text-secondary font-medium">
                                <span className="pl-8">SUBNET</span>
                                <span>CIDR</span>
                                <span>AZ</span>
                                <span>TYPE</span>
                                <span>IPs</span>
                                <span>STATE</span>
                                <span></span>
                              </div>
                            </td>
                          </tr>
                          {vpc.subnets.map((subnet: SubnetItem) => (
                            <SubnetRow
                              key={subnet.id}
                              subnet={subnet}
                              onClick={() => handleSubnetClick(subnet, vpc)}
                              href={topologyPath(vpc, subnet.id)}
                            />
                          ))}
                        </>
                      )}

                      {/* No subnets message */}
                      {isExpanded && (!vpc.subnets || vpc.subnets.length === 0) && (
                        <tr className="bg-semantic-bg-tertiary/50">
                          <td colSpan={7} className="px-6 py-4 text-center text-sm text-semantic-text-secondary">
                            No subnets found in this VPC/VCN
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Empty State */}
          {filteredVpcs.length === 0 && (
            <div className="px-6 py-12 text-center">
              <Search className="w-12 h-12 text-semantic-text-secondary mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-medium text-semantic-text-primary">No results found</h3>
              <p className="text-sm text-semantic-text-secondary mt-1">
                Try adjusting your search or filters
              </p>
              <button
                onClick={clearFilters}
                className="mt-4 px-4 py-2 bg-semantic-bg-brand-bold text-semantic-fg-base rounded-lg text-sm font-medium hover:opacity-90"
              >
                Clear filters
              </button>
            </div>
          )}

          {/* Footer with count */}
          {filteredVpcs.length > 0 && (
            <div className="px-6 py-3 bg-semantic-bg-tertiary border-t border-semantic-border-secondary text-sm text-semantic-text-secondary">
              Showing {filteredVpcs.length} VPCs/VCNs with{' '}
              {filteredVpcs.reduce((sum: number, v: VpcSecurityItem) => sum + (v.subnets?.length || 0), 0)} total subnets
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomeViewV2;

